import { getPlayerImageUrl } from "../../../helper/helper";
import type { IBattingScoreCardItem, IBowlingScoreCardItem, ICommentaryBall, IMatchCommentary, IMatchDetails, IMatchOfficial, InningScorecard } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";

export const LIVE_MATCH_POLL_MS = 30_000;

/** Fields the match endpoint returns but the shared type does not declare. */
export type MatchExtras = {
  leagueId?: string | null;
  matchOvers?: number | null;
  teamOneWinningProbability?: number | null;
  teamTwoWinningProbability?: number | null;
  /** Deduped page views (one per viewer per 15 min); null when the counter is unavailable. */
  viewCount?: number | null;
  showWinProbability?: boolean | null;
  youtubeStreamUrl?: string | null;
  streamURL?: string | null;
  hasLiveStream?: boolean | null;
  officials?: { role?: string | null; name?: string | null; profileId?: string | null }[] | null;
  tournament?: { tournamentId?: string | null; tournamentName?: string | null; leagueId?: string | null; leagueName?: string | null; maxOvers?: number | null };
};
export type MatchInfo = IMatchDetails & MatchExtras;

export type Ball = ICommentaryBall;

const PALETTE = ["#2B4C8C", "#3D6B4F", "#5B4A8F", "#A35A1F", "#7E3A55", "#2C6A80", "#8A6D24", "#4A5262"];
const SOFT = ["#E7EDF8", "#E6EFE9", "#ECE9F5", "#F6ECE3", "#F3E8ED", "#E4EFF3", "#F4EFE1", "#ECEEF1"];
const hashOf = (s?: string | null) => [...(s ?? "")].reduce((a, c) => a + c.charCodeAt(0), 0);
export const colorFor = (s?: string | null) => PALETTE[hashOf(s) % PALETTE.length];
export const softFor = (s?: string | null) => SOFT[hashOf(s) % SOFT.length];

export const initials = (name?: string | null, n = 2) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, n)
    .toUpperCase() || "?";

export const teamCode = (name?: string | null, code?: string | null) => (code && code.trim() ? code.trim().slice(0, 3).toUpperCase() : initials(name, 3));

const clean = (s?: string | null) => {
  const t = (s ?? "").trim();
  return !t || t.toLowerCase() === "null" || t.toLowerCase() === "undefined" ? "" : t;
};
export const fullName = (first?: string | null, last?: string | null) => [clean(first), clean(last)].filter(Boolean).join(" ");
export const officialName = (o?: IMatchOfficial | string | null) => {
  if (!o) return "";
  if (typeof o === "string") return clean(o);
  return fullName(o.playerFirstName, o.playerLastName) || clean(o.name);
};

export const fmtViews = (n: number) => (n < 1000 ? String(n) : n < 1_000_000 ? `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, "")}K` : `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`);

export const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

export const isLiveInning = (i?: InningScorecard | null) => i?.inningStatus === "IN_PROGRESS" || (i?.inningStatus as string) === "STARTED";

/** Combine getMatchInfo (full batting/bowling lists) with getScoreByMatch (live over/ball data) per innings. */
export function mergeInnings(info?: IMatchDetails, scores?: InningScorecard[]): InningScorecard[] {
  const infoList = info?.inningScorecardList ?? [];
  const scoreList = scores ?? [];
  const base = scoreList.length ? scoreList : infoList;
  return base.map((s) => {
    const i = infoList.find((x) => x.inningId === s.inningId);
    if (!i) return s;
    return {
      ...i,
      ...s,
      battingScoreCardResponseList: i.battingScoreCardResponseList?.length ? i.battingScoreCardResponseList : s.battingScoreCardResponseList,
      bowlingScoreCardResponses: i.bowlingScoreCardResponses?.length ? i.bowlingScoreCardResponses : s.bowlingScoreCardResponses,
      fallOfWickets: (s.fallOfWickets?.length ? s.fallOfWickets : i.fallOfWickets) ?? [],
      striker: s.striker ?? i.striker,
      nonStriker: s.nonStriker ?? i.nonStriker,
      currentOverBalls: s.currentOverBalls ?? i.currentOverBalls,
      lastOverBalls: s.lastOverBalls ?? i.lastOverBalls,
      equationRun: s.equationRun ?? i.equationRun,
    };
  });
}

export const inningOrdinal = (i: number) => (i === 0 ? "1st innings" : i === 1 ? "2nd innings" : `Innings ${i + 1}`);
export const scoreText = (i?: InningScorecard | null) => (i ? `${i.runs ?? 0}/${i.wicket ?? 0}` : "-");
export const oversText = (i?: InningScorecard | null) => (i ? `${i.over ?? 0}.${i.ball ?? 0}` : "0.0");
export const legalBallsOf = (i?: InningScorecard | null) => (i ? (i.over ?? 0) * 6 + (i.ball ?? 0) : 0);

export const isDismissed = (b: IBattingScoreCardItem) => {
  if (b.status === "STRIKER" || b.status === "NON_STRIKER") return false;
  const o = (b.outString ?? "").trim();
  return !!o && !/not\s*out|batting|did not bat|yet to bat/i.test(o);
};
export const batterName = (b: IBattingScoreCardItem) => fullName(b.playerFirstName, b.playerLastName) || "-";
export const bowlerName = (b: IBowlingScoreCardItem) => fullName(b.playerFirstName, b.playerLastName) || "-";
export const srOf = (r?: number | null, b?: number | null) => (b ? (((r ?? 0) * 100) / b).toFixed(1) : "-");
export const econOf = (b: IBowlingScoreCardItem) => {
  if (b.economy != null && Number(b.economy) > 0) return Number(b.economy).toFixed(2);
  const [o, ba] = String(b.over ?? "0").split(".");
  const balls = (Number(o) || 0) * 6 + (Number(ba) || 0);
  return balls ? (((b.runs ?? 0) * 6) / balls).toFixed(2) : "-";
};

export const isLegalBall = (b: Ball) => {
  const t = b?.extra?.type;
  return t !== "WIDE" && t !== "NO_BALL";
};
export const isWicketBall = (b: Ball) => b?.wicket?.wicket_id != null;

export const sortBalls = (balls: Ball[]) =>
  [...balls].sort((a, b) => {
    const tA = Number(a?.createdAt);
    const tB = Number(b?.createdAt);
    if (!Number.isNaN(tA) && !Number.isNaN(tB) && tA !== tB) return tA - tB;
    return (a?.ball_id ?? 0) - (b?.ball_id ?? 0);
  });

/** Chip text + CSS modifier for one delivery. */
export function ballChip(b: Ball): { text: string; cls: string } {
  if (isWicketBall(b)) return { text: "W", cls: "w" };
  const t = b?.extra?.type;
  const runs = b?.runs_scored ?? 0;
  if (t === "WIDE") return { text: runs > 0 ? `${runs}wd` : "Wd", cls: "x" };
  if (t === "NO_BALL") return { text: runs > 0 ? `${runs}nb` : "Nb", cls: "x" };
  if (t === "BYE") return { text: `${runs || b?.extra?.extraValue || 1}b`, cls: "x" };
  if (t === "LEG_BYE") return { text: `${runs || b?.extra?.extraValue || 1}lb`, cls: "x" };
  if (runs === 6) return { text: "6", cls: "s" };
  if (runs === 4) return { text: "4", cls: "f" };
  if (runs === 0) return { text: "•", cls: "d" };
  return { text: String(runs), cls: "" };
}

export const ballLabel = (b: Ball, over?: number) => `${b?.overNumber ?? over ?? 0}.${b?.ball_number ?? "-"}`;

/** Some backends number the first over 0, others 1. Mirror the old commentary tab. */
export function overIndexMode(overs: IMatchCommentary[]): "zero" | "one" {
  let s0 = false;
  let s1 = false;
  for (const o of overs) {
    if (o?.overNumber === 0) s0 = true;
    if (o?.overNumber === 1) s1 = true;
    for (const b of o?.ballsResponseList ?? []) {
      if (b?.overNumber === 0) s0 = true;
      if (b?.overNumber === 1) s1 = true;
    }
  }
  if (s0) return "zero";
  if (s1) return "one";
  return "zero";
}
export const overHeading = (n: number | undefined, mode: "zero" | "one") => (typeof n !== "number" || Number.isNaN(n) ? 1 : mode === "one" ? Math.max(1, n) : n + 1);

// the API sends inningType (NORMAL / SUPER_OVER) though the shared type does not declare it
const inningTypeOf = (i?: InningScorecard | null) => String((i as { inningType?: string | null } | null | undefined)?.inningType ?? "NORMAL").toUpperCase();
const hasSuperOver = (innings?: InningScorecard[] | null) => (innings ?? []).some((i) => inningTypeOf(i) === "SUPER_OVER");

/**
 * Win probabilities as whole percentages for [teamOne, teamTwo], or null when not shown. T20 only (the model is
 * trained on T20), and hidden once a super over starts, where the prediction no longer applies.
 * Pass the merged live innings when you have them: they are fresher than the match's own list.
 */
export function winProbability(m?: MatchInfo, innings?: InningScorecard[]): [number, number] | null {
  if (!m || !m.showWinProbability) return null;
  if (String(m.matchType ?? "").toUpperCase() !== "T20") return null;
  if (hasSuperOver(innings) || hasSuperOver(m.inningScorecardList)) return null;
  const a = m.teamOneWinningProbability;
  const b = m.teamTwoWinningProbability;
  if (a == null && b == null) return null;
  let x = a ?? (b != null ? (b <= 1 ? 1 - b : 100 - b) : 0);
  let y = b ?? (a != null ? (a <= 1 ? 1 - a : 100 - a) : 0);
  if (x + y <= 1.5) {
    x *= 100;
    y *= 100;
  }
  return [Math.round(x), Math.round(y)];
}

export const fmtDate = (s?: string | null) => {
  if (!s) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};
export const fmtTime = (s?: string | null) => {
  if (!s) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};
export const enumLabel = (v?: string | null) =>
  clean(v)
    .split("_")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");

export const roleTags = (role?: string | null) => {
  const r = (role ?? "").toUpperCase();
  return { c: r.includes("CAPTAIN") && !r.includes("VICE"), vc: r.includes("VICE"), wk: r.includes("WICKET") };
};

export type Row = [string, string];
const groundName = (m?: MatchInfo) => clean((m?.groundId ?? m?.teamOne?.groundId ?? m?.teamTwo?.groundId)?.groundName);

export const venueShort = (m?: MatchInfo) => groundName(m) || clean(m?.city);

/** Test / multi-day: the stored over limit (e.g. 450) is a cap, not something to show. */
export const isMultiDayFormat = (m?: { matchType?: string | null; scheduledDays?: number | null } | null) =>
  /TEST/i.test(m?.matchType ?? "") || (m?.scheduledDays ?? 1) > 1;

export function matchRows(m?: MatchInfo): Row[] {
  if (!m) return [];
  const rows: Row[] = [
    ["Format", enumLabel(m.matchType)],
    ["Overs", m.matchOvers && !isMultiDayFormat(m) ? String(m.matchOvers) : ""],
    ["Date", fmtDate(m.startDate)],
    ["Start", fmtTime(m.startDate)],
    ["Ground", groundName(m)],
    ["City", clean(m.city)],
    ["Toss", clean(m.tossResult)],
    ["Pitch", enumLabel(m.pitchType)],
  ];
  return rows.filter(([, v]) => !!v);
}

// MatchOfficialRole (backend enum) -> label, in display order
const OFFICIAL_ROLES: [string, string][] = [
  ["ON_FIELD_UMPIRE_1", "Umpire"], ["ON_FIELD_UMPIRE_2", "Second umpire"], ["THIRD_UMPIRE", "Third umpire"], ["FOURTH_UMPIRE", "Fourth umpire"],
  ["MATCH_REFEREE", "Match referee"], ["SCORER_1", "Scorer"], ["SCORER_2", "Second scorer"],
  ["COMMENTATOR_1", "Commentator"], ["COMMENTATOR_2", "Second commentator"], ["STREAMER_1", "Streamer"], ["STREAMER_2", "Second streamer"],
];

/**
 * Match officials as [label, name] rows. Officials set from the app's officials picker only come back in
 * `officials[]` (the legacy umpire / scorer fields stay null), so read that first and fall back to the legacy fields.
 */
export function officialRows(m?: MatchInfo): Row[] {
  if (!m) return [];
  const list = m.officials ?? [];
  const rows: Row[] = OFFICIAL_ROLES.map(([role, label]) => [label, clean(list.find((o) => o.role === role)?.name)]);
  list.filter((o) => !OFFICIAL_ROLES.some(([r]) => r === o.role)).forEach((o) => rows.push([enumLabel(o.role) || "Official", clean(o.name)]));
  const legacy: Row[] = [
    ["Umpire", officialName(m.umpire ?? m.availableUmpireResponse)],
    ["Second umpire", officialName(m.umpireTwo)],
    ["Third umpire", officialName(m.thirdUmpire)],
    ["Scorer", officialName(m.scorer)],
  ];
  legacy.forEach(([k, v]) => { if (v && !rows.some(([rk, rv]) => rk === k && rv)) rows.push([k, v]); });
  return rows.filter(([, v]) => !!v);
}

/**
 * The prediction's projected total for the side batting first (winPredictionMetadata.projected_score), only while
 * that first innings is on: once the last innings starts the target replaces it.
 */
export function projectedScore(m: MatchInfo | undefined, innings: InningScorecard[], liveIndex: number): { runs: number; over?: number } | null {
  const meta = (m as { winPredictionMetadata?: Record<string, unknown> | null } | undefined)?.winPredictionMetadata;
  const runs = Number(meta?.projected_score);
  if (!meta || meta.projected_score == null || !Number.isFinite(runs)) return null;
  const inn = innings[liveIndex];
  if (liveIndex !== 0 || !inn || !isLiveInning(inn) || inningTypeOf(inn) !== "NORMAL") return null;
  const over = Number(meta.cur_over);
  return { runs: Math.round(runs), over: Number.isFinite(over) && over > 0 ? over : undefined };
}

export type TeamRef = { name: string; code: string; image: string };

export const teamRefs = (m?: MatchInfo): TeamRef[] =>
  [m?.teamOne, m?.teamTwo].map((t) => ({ name: t?.teamName ?? "", code: teamCode(t?.teamName, t?.teamCode), image: getPlayerImageUrl(t?.imageUrl) ?? "" }));

export const teamByName = (m: MatchInfo | undefined, name?: string | null): TeamRef => {
  const refs = teamRefs(m);
  return refs.find((r) => norm(r.name) === norm(name)) ?? { name: name ?? "", code: teamCode(name), image: "" };
};


